import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Card from '@material-ui/core/Card';
import { reduxForm, Field, getFormValues } from 'redux-form';
import { withStyles } from '@material-ui/core/styles';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import withWidth from '@material-ui/core/withWidth';
import CircularProgress from '@material-ui/core/CircularProgress';
import GoogleMapReact from 'google-map-react';

import styles from './styles';
import {
  editUserProfile,
  editUserProfileCancelled,
  updateBusinessAttributes,
} from '../../../store/actions/userProfile';
import {
  selectPersonalProfileFormMode,
  selectBusinessProfileFormMode,
  selectServiceAreaFormMode,
  selectBusinessAttributes,
  selectUserAttributes,
  selectIsUpdatingServiceArea,
} from '../../../store/selectors';
import {
  FORMS,
  AUS_STATES,
  SERVICE_AREAS,
  GOOGLE_MAPS_API_KEY,
  LOCAL_SERVICE_AREA_RADIUS_KM,
  AUS_CENTER,
  SOUTH_POLE,
  VIEWABLE_FORM_MODES,
  USER_PROFILE_TYPES,
} from '../../../constants';
import Button from '../../Button';
import { required, coordRequired } from '../../../utilities/validators';
import PlaceSearchFormField from '../../../fields/reduxForm/PlaceSearchFormField';
import SelectFormField from '../../../fields/reduxForm/SelectFormField';
import ServiceAreaButtons from '../../../fields/reduxForm/ServiceAreaButtons';
import HiddenTextField from '../../../fields/reduxForm/HiddenTextField';

const propTypes = {
  classes: PropTypes.shape({
    cardHeaderRoot: PropTypes.string,
    cardHeaderTitle: PropTypes.string,
    formGroup: PropTypes.string,
    halfFormGroup: PropTypes.string,
    formRow: PropTypes.string,
  }).isRequired,
  formValues: PropTypes.object,
  mapDefaultCenter: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  mapDefaultZoom: PropTypes.number,
  mapCenter: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  width: PropTypes.string.isRequired,
  personalProfileFormMode: PropTypes.oneOf([
    VIEWABLE_FORM_MODES.EDITING,
    VIEWABLE_FORM_MODES.VIEWING,
  ]).isRequired,
  businessProfileFormMode: PropTypes.oneOf([
    VIEWABLE_FORM_MODES.EDITING,
    VIEWABLE_FORM_MODES.VIEWING,
  ]).isRequired,
  serviceAreaFormMode: PropTypes.oneOf([
    VIEWABLE_FORM_MODES.EDITING,
    VIEWABLE_FORM_MODES.VIEWING,
  ]).isRequired,
  onEditUserProfile: PropTypes.func.isRequired,
  onEditUserProfileCancelled: PropTypes.func.isRequired,
  isUpdatingServiceArea: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onUpdateBusinessAttributes: PropTypes.func.isRequired,
  businessAttributes: PropTypes.object.isRequired,
  userAttributes: PropTypes.object.isRequired,
  change: PropTypes.func.isRequired,
  touch: PropTypes.func.isRequired,
  untouch: PropTypes.func.isRequired,
};

const defaultProps = {
  formValues: {},
  mapDefaultCenter: AUS_CENTER,
  mapDefaultZoom: 4,
};

const mapCircleInitialCenter = [SOUTH_POLE.lat, SOUTH_POLE.lng];

export const ServiceAreaForm = props => {
  const {
    classes,
    formValues,
    mapDefaultCenter,
    mapDefaultZoom,
    width,
    change,
    touch,
    untouch,
    personalProfileFormMode,
    onEditUserProfile,
    onEditUserProfileCancelled,
    handleSubmit,
    onUpdateBusinessAttributes,
    businessProfileFormMode,
    serviceAreaFormMode,
    businessAttributes,
    userAttributes,
    isUpdatingServiceArea,
  } = props;

  useEffect(
    () => () => {
      handleFormCancel();
    },
    [],
  );

  const [cachedMap, setCachedMap] = useState();
  const [cachedMaps, setCachedMaps] = useState();
  const [cachedMapCircle, setCachedMapCircle] = useState();
  // eslint-disable-next-line
  const [initialValues, setInitialValues] = useState({
    businessServiceAreaType: '',
    businessServiceAreaAddress: '',
    businessServiceAreaCoordinates: '',
    businessServiceAreaState: '',
  });

  // console.log({ initialValues });
  useEffect(() => {
    setInitialValues({
      businessServiceAreaType: businessAttributes.businessServiceAreaType,
      businessServiceAreaAddress: businessAttributes.businessServiceAreaAddress,
      businessServiceAreaCoordinates:
        businessAttributes.businessServiceAreaCoordinates,
      businessServiceAreaState: businessAttributes.businessServiceAreaState,
    });
    change(
      'businessServiceAreaState',
      businessAttributes.businessServiceAreaState,
    );
    change(
      'businessServiceAreaAddress',
      businessAttributes.businessServiceAreaAddress,
    );
    // eslint-disable-next-line prettier/prettier
    change(
      'businessServiceAreaCoordinates',
      businessAttributes.businessServiceAreaCoordinates,
    );
    change(
      'businessServiceAreaType',
      businessAttributes.businessServiceAreaType,
    );

    // Initial Form
    prepareMapForDisplay(cachedMap, cachedMaps, cachedMapCircle);
  }, [
    businessAttributes.businessServiceAreaState,
    businessAttributes.businessServiceAreaType,
    businessAttributes.businessServiceAreaAddress,
    businessAttributes.businessServiceAreaCoordinates,
  ]);

  const correctZoom = zoomLevel => {
    let correctZoomLevel = zoomLevel;
    if (width === 'xs' || width === 'sm') {
      correctZoomLevel -= 1;
    } else if (width === 'md') {
      correctZoomLevel += 1;
    }
    return correctZoomLevel;
  };

  const resetLocalMap = () => {
    if (cachedMap && cachedMaps) {
      cachedMap.setCenter(mapDefaultCenter);
      cachedMap.setZoom(correctZoom(mapDefaultZoom));
      if (cachedMapCircle) {
        const newC = new cachedMaps.LatLng(mapCircleInitialCenter);
        cachedMapCircle.setCenter(newC);
      }
      // eslint-disable-next-line no-console
    } else console.warn('cachedMap && cachedMaps not set');
  };

  const setLocalMap = (latLong, map, maps, mapCircle) => {
    if (map && maps) {
      if (mapCircle) {
        const newC = new maps.LatLng(latLong);
        mapCircle.setCenter(newC);
        map.fitBounds(mapCircle.getBounds());
        if (width === 'xs' || width === 'sm') {
          map.setZoom(9);
        }
        // eslint-disable-next-line no-console
      } else console.warn('mapCircle not set');
      // eslint-disable-next-line no-console
    } else console.warn('cachedMap && cachedMaps not set');
  };

  const handleServiceAreaButtons = type => {
    switch (type) {
      case SERVICE_AREAS.LOCAL:
        handleLocal();
        break;
      case SERVICE_AREAS.STATEWIDE:
        handleStatewide();
        break;
      case SERVICE_AREAS.NATIONAL:
        handleNational();
        break;
      default:
    }
  };

  const handleNational = () => {
    change('businessServiceAreaState', '');
    handleClearCoord();
    change('businessServiceAreaAddress', '');
  };
  const handleStatewide = () => {
    resetLocalMap();
    handleClearCoord();
    change('businessServiceAreaAddress', '');
    // reset drop down
  };
  const handleStateSelect = (selectedState, map) => {
    let thisMap = map;
    if (!map) thisMap = cachedMap;
    const state = AUS_STATES.filter(obj => obj.short_name === selectedState)[0];
    // move map to location in AUS_STATES
    if (thisMap && state) {
      thisMap.setCenter({
        lat: state.center_location.lat,
        lng: state.center_location.lng,
      });
      thisMap.setZoom(correctZoom(state.zoom_level));
    } else if (!state) {
      resetLocalMap();
    }
  };
  const handleLocal = () => {
    change('businessServiceAreaState', '');
    resetLocalMap();
    untouch('businessServiceAreaCoordinates');
  };

  const handleGettingCoord = latLong => {
    change('businessServiceAreaCoordinates', `${latLong.lat}, ${latLong.lng}`);
    setLocalMap(latLong, cachedMap, cachedMaps, cachedMapCircle);
    change('businessServiceAreaState', '');
  };

  const handleClearCoord = () => {
    change('businessServiceAreaCoordinates', '');
    resetLocalMap();
  };

  const handleServiceAreaChange = () => {
    touch('businessServiceAreaCoordinates');
  };

  const getStateOptions = () => {
    const options = AUS_STATES.map(state => (
      <option value={state.short_name}>{state.long_name}</option>
    ));
    return options;
  };

  const getCircleProps = (map, center) => ({
    strokeColor: '#979797',
    strokeOpacity: 1,
    strokeWeight: 0.7,
    fillColor: '#009a44',
    fillOpacity: 0.15,
    map,
    center,
    radius: LOCAL_SERVICE_AREA_RADIUS_KM * 1000,
  });

  const newMapCircle = (map, maps, center) =>
    new maps.Circle(getCircleProps(map, center));

  const apiIsLoaded = (map, maps) => {
    map.set('disableDefaultUI', true);
    map.set('gestureHandling', 'none');
    map.set('zoomControl', false);

    const newCircle = newMapCircle(map, maps, mapCircleInitialCenter);

    setCachedMap(map);
    setCachedMaps(maps);
    setCachedMapCircle(newCircle);

    prepareMapForDisplay(map, maps, newCircle);
  };

  const prepareMapForDisplay = (map, maps, mapCircle) => {
    if (businessAttributes.businessServiceAreaType === SERVICE_AREAS.LOCAL) {
      const latLng = {
        // eslint-disable-next-line prettier/prettier
        lat: parseFloat(
          businessAttributes.businessServiceAreaCoordinates.split(',')[0],
        ),
        // eslint-disable-next-line prettier/prettier
        lng: parseFloat(
          businessAttributes.businessServiceAreaCoordinates.split(',')[1],
        ),
      };
      if (map) {
        setLocalMap(latLng, map, maps, mapCircle);
      }
      // eslint-disable-next-line prettier/prettier
    } else if (
      businessAttributes.businessServiceAreaType === SERVICE_AREAS.STATEWIDE
    ) {
      const state = AUS_STATES.filter(
        obj => obj.short_name === businessAttributes.businessServiceAreaState,
      )[0];
      if (map) {
        map.setCenter({
          lat: state.center_location.lat,
          lng: state.center_location.lng,
        });
        map.setZoom(correctZoom(state.zoom_level));
      }
    }
  };
  // console.log({ businessAttributes });
  // Form Stuff

  const handleFormSubmitted = values => {
    const stateValues = {
      businessNumber: userAttributes.sub,
      businessPhoneNumber:
        userAttributes['custom:mobileNumber'] ||
        userAttributes['custom:personalPhone'],
      ...businessAttributes,
      coverPhotoImage: businessAttributes.coverPhoto,
      logoImage: businessAttributes.logo,
      galleryImages:
        businessAttributes.gallery &&
        businessAttributes.gallery[0] !== null &&
        businessAttributes.gallery.map(item => ({
          image: item.file,
          id: item.id,
        })),
      // businessName: businessAttributes.businessName,
      // businessPosition: businessAttributes.businessPosition,
      // businessDescription: businessAttributes.businessDescription,
    };
    let cleanedValues;
    if (values.businessServiceAreaType === SERVICE_AREAS.STATEWIDE) {
      cleanedValues = {
        ...stateValues,
        ...values,
        businessServiceAreaAddress: '',
        businessServiceAreaCoordinates: '',
      };
    } else if (values.businessServiceAreaType === SERVICE_AREAS.NATIONAL) {
      cleanedValues = {
        ...stateValues,
        ...values,
        businessServiceAreaAddress: '',
        businessServiceAreaCoordinates: '',
        businessServiceAreaState: '',
      };
    } else if (values.businessServiceAreaType === SERVICE_AREAS.LOCAL) {
      cleanedValues = {
        ...stateValues,
        ...values,
        businessServiceAreaState: '',
      };
    }

    let previousCleanedValues;
    if (initialValues.businessServiceAreaType === SERVICE_AREAS.STATEWIDE) {
      previousCleanedValues = {
        ...stateValues,
        ...initialValues,
        businessServiceAreaAddress: '',
        businessServiceAreaCoordinates: '',
      };
    } else if (
      initialValues.businessServiceAreaType === SERVICE_AREAS.NATIONAL
    ) {
      previousCleanedValues = {
        ...stateValues,
        ...initialValues,
        businessServiceAreaAddress: '',
        businessServiceAreaCoordinates: '',
        businessServiceAreaState: '',
      };
    } else if (initialValues.businessServiceAreaType === SERVICE_AREAS.LOCAL) {
      previousCleanedValues = {
        ...stateValues,
        ...initialValues,
        businessServiceAreaState: '',
      };
    }
    // console.log({ previousCleanedValues, cleanedValues });
    onUpdateBusinessAttributes(
      localStorage.getItem('accessToken'),
      USER_PROFILE_TYPES.SERVICE_AREA,
      cleanedValues,
      previousCleanedValues,
      previousCleanedValues,
    );
  };

  const canEnableEditing = profileType => {
    switch (profileType) {
      case USER_PROFILE_TYPES.PERSONAL:
        return (
          businessProfileFormMode !== VIEWABLE_FORM_MODES.EDITING &&
          serviceAreaFormMode !== VIEWABLE_FORM_MODES.EDITING
        );
      case USER_PROFILE_TYPES.BUSINESS:
        return (
          personalProfileFormMode !== VIEWABLE_FORM_MODES.EDITING &&
          serviceAreaFormMode !== VIEWABLE_FORM_MODES.EDITING
        );
      case USER_PROFILE_TYPES.SERVICE_AREA:
        return (
          personalProfileFormMode !== VIEWABLE_FORM_MODES.EDITING &&
          businessProfileFormMode !== VIEWABLE_FORM_MODES.EDITING
        );
      default:
        // eslint-disable-next-line no-console
        console.log('reach default case');
        return true;
    }
  };

  const readOnly = () => {
    if (serviceAreaFormMode !== VIEWABLE_FORM_MODES.EDITING) return true;
    return false;
  };

  const handleFormCancel = () => {
    // Reset form values
    change(
      'businessServiceAreaState',
      businessAttributes.businessServiceAreaState,
    );
    change(
      'businessServiceAreaAddress',
      businessAttributes.businessServiceAreaAddress,
    );
    // eslint-disable-next-line prettier/prettier
    change(
      'businessServiceAreaCoordinates',
      businessAttributes.businessServiceAreaCoordinates,
    );
    change(
      'businessServiceAreaType',
      businessAttributes.businessServiceAreaType,
    );

    onEditUserProfileCancelled(USER_PROFILE_TYPES.SERVICE_AREA);
  };

  const handleClick = () => {
    onEditUserProfile(USER_PROFILE_TYPES.SERVICE_AREA);
  };

  const renderFormButton = () => {
    if (serviceAreaFormMode === VIEWABLE_FORM_MODES.VIEWING) {
      return (
        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={handleClick}
          fullWidth
          disabled={!canEnableEditing(USER_PROFILE_TYPES.SERVICE_AREA)}
        >
          Edit
        </Button>
      );
    }

    return (
      <div className={classes.formButtons}>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={handleFormCancel}
          disabled={
            !canEnableEditing(USER_PROFILE_TYPES.SERVICE_AREA) ||
            isUpdatingServiceArea
          }
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleFormSubmitted)}
          disabled={
            !canEnableEditing(USER_PROFILE_TYPES.SERVICE_AREA) ||
            isUpdatingServiceArea
          }
        >
          {isUpdatingServiceArea && <CircularProgress size={20} />}
          {!isUpdatingServiceArea && 'Done'}
        </Button>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className={classes.root}>
      <div className={classes.serviceAreaContain}>
        <Card className={classes.serviceAreaControlsWrapper}>
          <CardHeader
            classes={{
              root: classes.cardHeaderRoot,
              title: classes.cardHeaderTitle,
            }}
            title="Service Area"
          />
          <div className={classes.serviceAreaForm}>
            <CardContent className={classes.serviceAreasCardContent}>
              <Typography variant="h6">Locations</Typography>
              {!readOnly() && (
                <Typography variant="body1">Please select</Typography>
              )}
              <div
                className={classes.formRow}
                data-test="business-address-field-group"
              >
                <div className={classes.formGroup}>
                  <Field
                    name="businessServiceAreaType"
                    classes={classes}
                    component={ServiceAreaButtons}
                    validate={[required]}
                    onChange={handleServiceAreaButtons}
                    enableEditing={readOnly()}
                  />
                  {formValues.businessServiceAreaType ===
                    SERVICE_AREAS.STATEWIDE && (
                    <div className={classes.stateSelectContain}>
                      <Field
                        classes={classes}
                        name="businessServiceAreaState"
                        component={SelectFormField}
                        label="State"
                        validate={[required]}
                        enableEditing={readOnly()}
                        props={{
                          id: 'service-area-state-select-field',
                          label: 'Select State',
                          onSelectChange: handleStateSelect,
                          onClear: handleClearCoord,
                          onBlur: handleServiceAreaChange,
                        }}
                      >
                        <option />
                        {getStateOptions()}
                      </Field>
                    </div>
                  )}
                  {formValues.businessServiceAreaType ===
                    SERVICE_AREAS.LOCAL && (
                    <>
                      <Field
                        name="businessServiceAreaAddress"
                        component={PlaceSearchFormField}
                        enableEditing={readOnly()}
                        props={{
                          id: 'service-area-address-text-field',
                          label: 'Service Area Address',
                          fullWidth: true,
                          onGettingCoord: handleGettingCoord,
                          onClearCoord: handleClearCoord,
                          onFieldBlur: handleServiceAreaChange,
                        }}
                      />
                      <div className={classes.serviceAreaCoord}>
                        <Field
                          name="businessServiceAreaCoordinates"
                          component={HiddenTextField}
                          props={{
                            id: 'service-area-coord-text-field',
                            fullWidth: true,
                          }}
                          validate={[coordRequired]}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className={classes.editButton}>{renderFormButton()}</div>
            </CardContent>
          </div>
        </Card>
        <div className={classes.mapWrapper}>
          <div className={classes.serviceAreaMapContain}>
            <div className={classes.serviceAreaMap}>
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: GOOGLE_MAPS_API_KEY,
                }}
                defaultCenter={mapDefaultCenter}
                defaultZoom={correctZoom(mapDefaultZoom)}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

ServiceAreaForm.propTypes = propTypes;
ServiceAreaForm.defaultProps = defaultProps;

const mapStateToProps = state => ({
  isUpdatingServiceArea: selectIsUpdatingServiceArea()(state),
  formValues: getFormValues(FORMS.UPDATE_SERVICE_AREA)(state),
  personalProfileFormMode: selectPersonalProfileFormMode()(state),
  businessProfileFormMode: selectBusinessProfileFormMode()(state),
  serviceAreaFormMode: selectServiceAreaFormMode()(state),
  businessAttributes: selectBusinessAttributes()(state),
  userAttributes: selectUserAttributes()(state),
});

const mapDispatchToProps = dispatch => ({
  onEditUserProfile: profileType => dispatch(editUserProfile(profileType)),
  onEditUserProfileCancelled: profileType =>
    dispatch(editUserProfileCancelled(profileType)),
  onUpdateBusinessAttributes: (
    accessToken,
    profileType,
    businessAttributes,
    previousBuisnessAttributes,
    previousCleanedValues,
  ) =>
    dispatch(
      updateBusinessAttributes(
        accessToken,
        profileType,
        businessAttributes,
        previousBuisnessAttributes,
        previousCleanedValues,
      ),
    ),
});

export default reduxForm({
  form: FORMS.UPDATE_SERVICE_AREA,
})(
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(withWidth()(ServiceAreaForm)),
  ),
);
